import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { compose } from 'recompose'
import { withRouter } from 'react-router'
import { injectIntl, defineMessages } from 'react-intl'
import _ from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { fields } from '../../common/lib/redux-fields'
import { validateCreateWordbox } from '../../common/lessons/validations'
import { createLesson } from '../../common/lessons/actions'
import { updateMe } from '../../common/users/actions'
import formValidation from '../../common/lib/formValidation'
import { exerciseTypes } from '../../common/config'
import { LessonFormHeader } from '../forms'
import { withNameInput, Popup, Button, TextInput } from '../components'

function indexesWithNeighbours(text, fillIndexes) {
  const updatedIndexes = []

  let i = 0
  while (i < text.length) {
    if (fillIndexes.includes(i) && fillIndexes.includes(i + 1)) {
      let to = i + 1
      while (fillIndexes.includes(to)) {
        to += 1
      }
      updatedIndexes.push({ from: i, to })
      i = to
    } else if (fillIndexes.includes(i)) {
      updatedIndexes.push(i)
    }
    i += 1
  }

  return updatedIndexes
}

function flattenIndexes(fillIndexes) {
  return _.flatten(fillIndexes.map(id => {
    if (id.from) {
      return _.range(id.from, id.to)
    }
    return id
  }))
}

class WordboxForm extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      text: '',
      instructions: '',
      fillIndexes: [],
      wordIndexes: {},
      isPublic: true,
      showWarning: false,
      options: {},
    }
  }

  componentDidMount() {
    const { lesson } = this.props
    if (lesson) {
      const wordIndexes = {}
      lesson.wordbox.fillIndexes.forEach(key => {
        wordIndexes[key] = lesson.wordbox.text[key]
      })

      this.setState({
        id: lesson.id,
        isPublic: lesson.isPublic,
        instructions: lesson.instructions,
        text: lesson.wordbox.text,
        fillIndexes: lesson.wordbox.fillIndexes,
        options: lesson.wordbox.options || {},
        wordIndexes,
      })
    }
  }

  componentWillUnmount() {
    const { fields } = this.props
    fields.$reset()
  }

  addOption(index) {
    const { options, text } = this.state

    const wordIndex = index.from || index

    if (!options || !options[wordIndex]) {
      this.setState({
        options: { ...options, [wordIndex]: [index.from ? text.slice(index.from, index.to).join(' ') : text[wordIndex], ''] },
      })
    } else {
      this.setState({
        options: { ...options, [wordIndex]: [...options[wordIndex], ''] },
      })
    }
  }

  editOption(e, wordIndex, optionIndex) {
    const { value } = e.target
    const { options, text } = this.state
    const newOptions = options[wordIndex].map((o, oidx) => {
      if (oidx === optionIndex) {
        return value
      }
      return o
    })
    const newText = text
    if (optionIndex === 0) {
      newText[wordIndex] = value
    }
    this.setState({
      text: newText,
      options: { ...options, [wordIndex]: newOptions },
    })
  }

  removeOption(wordIndex, optionIndex) {
    const { options } = this.state

    const newWordOptions = options[wordIndex]

    newWordOptions.splice(optionIndex, 1)

    if (newWordOptions.length === 1) {
      const newOptions = Object.assign({}, options)
      delete newOptions[wordIndex]
      this.setState({
        options: newOptions,
      })
      return
    }

    this.setState({
      options: { ...options, [wordIndex]: newWordOptions },
    })
  }

  async submitForm(e) {
    e.preventDefault()
    const { fields, submit, history, isTestForm, hideForm } = this.props
    const { text, fillIndexes, id, isPublic, options } = this.state

    const values = {
      ...fields.$values(),
      id,
      isPublic,
      type: exerciseTypes.WORDBOX,
      text,
      fillIndexes,
      options,
    }
    if (await submit(values) !== false) {
      fields.$reset()
      if (!isTestForm) {
        history.push('/app/lessons')
      } else {
        hideForm()
      }
    }
  }

  handleIndexChange(index) {
    const { text, fillIndexes, wordIndexes, options } = this.state

    const flattenedIndexes = flattenIndexes(fillIndexes)

    if (flattenedIndexes.includes(index)) {
      const newOptions = options
      const newWordIndexes = Object.assign({}, wordIndexes)
      delete newWordIndexes[index]

      console.log('newOptions ', newOptions)
      /* if index in range and has options */
      const inRange = fillIndexes.find(range => range.from && range.from <= index && range.to > index)
      
      if (inRange && newOptions[inRange.from]) {
        delete newOptions[inRange.from]
      }

      const newFillIndexes = flattenedIndexes
                              .sort((a, b) => a - b)
                              .filter(item => item !== index)

      const updatedIndexes = indexesWithNeighbours(text, newFillIndexes)

      this.setState({
        text,
        options: newOptions,
        fillIndexes: updatedIndexes,
        wordIndexes: newWordIndexes,
      })
    } else {
      /* add word as gap */
      const newOptions = options
      const newFillIndexes = [...flattenedIndexes, index].sort((a, b) => a - b)
      const updatedIndexes = indexesWithNeighbours(text, newFillIndexes)
      
      const inRange = updatedIndexes.find(range => range.from && range.from <= index && range.to > index)

      if (inRange && newOptions[inRange.from]) {
        delete newOptions[inRange.from]
      }

      /* TODO if index in next range which has options */
      this.setState({
        text,
        fillIndexes: updatedIndexes,
        wordIndexes: { ...wordIndexes, [index]: text[index] },
      })
    }
  }

  handleFillUpChange(e) {
    const { text, fillIndexes, wordIndexes, showWarning } = this.state
    const newText = e.target.value
      .replace(/([a-z]|[0-9])[,]/g, '$1 ,')
      .replace(/([a-z]|[0-9])[.]/g, '$1 .')
      .replace(/([a-z]|[0-9])[!]/g, '$1 !')
      .replace(/([a-z]|[0-9])[?]/g, '$1 ?')
      .split(' ')

    let correctIndex = 0

    const allCorrect = (text, wordIndexes, fillIndexes, direction) => {
      for (let i = 0; i < fillIndexes.length; i += 1) {
        if (text[fillIndexes[i]] !== wordIndexes[fillIndexes[i] - direction]) {
          return false
        }
      }
      return true
    }

    fillIndexes.forEach(idx => {
      if (newText[idx] === wordIndexes[idx]) {
        correctIndex += 1
      }
    })

    let direction = 0
    if (text.length < newText.length) {
      direction = 1
    } else if (text.length > newText.length) {
      direction = -1
    }

    let restIndexes = fillIndexes.slice(correctIndex)

    while (direction) {
      restIndexes = restIndexes.map(idx => idx + direction)
      const isCorrect = allCorrect(newText, wordIndexes, restIndexes, direction)
      if (isCorrect || restIndexes[0] === 0 ||
        restIndexes[restIndexes.length - 1] === newText.length) {
        break
      }
    }

    const newFillIndexes = [...fillIndexes.slice(0, correctIndex), ...restIndexes]
      .filter(idx => idx < newText.length)

    const newWordIndexes = {}
    newFillIndexes.forEach(key => {
      newWordIndexes[key] = newText[key]
    })

    this.setState({
      text: newText,
      fillIndexes: newFillIndexes,
      wordIndexes: newWordIndexes,
      showWarning: showWarning ||
        (!!fillIndexes.length && text.length !== newText.length),
    })
  }

  render() {
    const { fields, errorMessage, lesson, viewer, intl,
      isDetailPage, updateMe } = this.props
    const { fillIndexes, text, options, showWarning } = this.state

    return (
      <div className="wordbox-form float-left">
        {
          !isDetailPage && viewer && !viewer.seenGuides.wordboxDescription &&
          <Popup
            title={intl.formatMessage(messages.popupTitle)}
            text={intl.formatMessage(messages.popupContent)}
            onClick={() => updateMe({ seenGuides: { wordboxDescription: true } })}
          />
        }
        <form className="flex-column" onSubmit={(e) => this.submitForm(e)}>
          <LessonFormHeader
            isDetailPage={isDetailPage}
            errors={errorMessage}
            fields={fields}
            lesson={lesson}
          />
          <h3 className="float-left" style={{ marginTop: '2rem' }}>{intl.formatMessage(messages.text)}</h3>
          <div className="wordbox-wrapper">
            <div className="wordbox-field">
              <p className="field-title">
                {intl.formatMessage(messages.enterText)}
              </p>
              <textarea
                className="wordbox-input"
                onChange={e => this.handleFillUpChange(e)}
                value={text && text.length && text.join(' ')}
                disabled={isDetailPage}
              />
            </div>
            <div className="wordbox-field">
              <p className="field-title">
                {intl.formatMessage(messages.markWords)}
              </p>
              <p className="wordbox-text">
                {text && text.map((w, i) => (
                  <span
                    key={`${w}${i}`}
                    onClick={() => this.handleIndexChange(i)}
                    className={fillIndexes.includes(i) ||
                      fillIndexes.some(range => range.from <= i && range.to > i) ? 'selected' : ''}
                  >
                    {w}
                  </span>
                ))}
              </p>
            </div>
          </div>
          {
            showWarning && text.length > 1 &&
            <div className="float-left width-full">
              <p className="warning-msg">{intl.formatMessage(messages.check)}</p>
            </div>
          }
          {
            !fillIndexes.length && text.length > 1 &&
            <div className="float-left width-full">
              <p className="warning-msg">{intl.formatMessage(messages.remind)}</p>
            </div>
          }

          <div className="float-left width-full vertical-spacing">
            {
              fillIndexes.map((index) => (
                <div key={index.from || index} className="wordbox-option">
                  {
                    options && options[index.from || index] ?
                      <div className="wordbox-option-options">
                        {
                          options[index.from || index].map((option, optionIndex) => (
                            <div className="wordbox-option-options-item" key={optionIndex}>
                              <TextInput
                                disabled={!optionIndex}
                                value={option}
                                onChange={(e) => this.editOption(e, index.from || index, optionIndex)}
                              />
                              {
                                !!optionIndex &&
                                <FontAwesomeIcon
                                  icon="trash"
                                  className="delete-icon"
                                  onClick={() => this.removeOption(index.from || index, optionIndex)}
                                />
                              }
                            </div>
                          ))
                        }
                      </div>
                    :
                      <TextInput
                        disabled
                        value={index.from ? text.slice(index.from, index.to).join(' ') : text[index]}
                      />
                  }
                  <Button onClick={() => this.addOption(index)}>
                    {intl.formatMessage(messages.addOption)}
                  </Button>
                </div>
              ))
            }
          </div>

          {
            !isDetailPage &&
            <div className="float-left block-center bottom-save-button">
              <Button big positive type="submit">
                {intl.formatMessage(messages.save)}
              </Button>
            </div>
          }
        </form>
      </div>
    )
  }
}

WordboxForm.defaultProps = {
  lesson: null,
  isDetailPage: false,
}

WordboxForm.propTypes = {
  intl: PropTypes.object.isRequired,
  fields: PropTypes.object.isRequired,
  viewer: PropTypes.object.isRequired,
  lesson: PropTypes.object,
  isDetailPage: PropTypes.bool,
  updateMe: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  errorMessage: PropTypes.any.isRequired,
}

WordboxForm = withRouter(WordboxForm)

const enhance = compose(
  withNameInput,
  formValidation(props => ({
    validationFunc: validateCreateWordbox,
    submitFunc: props.submitLesson,
  })),
  connect(state => ({
    viewer: state.users.viewer,
  }), { createLesson, updateMe }),
  fields({
    path: 'WordboxForm',
    fields: [
      'name',
      'instructions',
      'youtubeVideo',
      'prologueText',
      // 'subject',
      'tags',
    ],
    getInitialState: ({ lesson, viewer, ...props }) => ({
      name: (lesson && lesson.name) || props.name || '',
      instructions: ((lesson && lesson.instructions)
        ? lesson.instructions : ''),
      prologueText: (lesson && lesson.prologueText) || props.prologueText || '',
      youtubeVideo: (lesson && lesson.youtubeVideo) || props.youtubeVideo || '',
      tags: (lesson && lesson.tags) || props.tags || [],
      // subject: (lesson && lesson.subject) || props.subject || ((viewer.subjects.length && !lesson) ? viewer.subjects[0] : ''),
    }),
  }),
  injectIntl,
)

export default enhance(WordboxForm)

const messages = defineMessages({
  popupTitle: {
    id: 'wordbox.form.popupTitle',
    defaultMessage: 'Inštrukcie',
  },
  popupContent: {
    id: 'wordbox.form.popupContent',
    defaultMessage: 'Vložte text a v pravej časti kliknite na slová v texte, ktoré budú žiaci dopĺňať.',
  },
  text: {
    id: 'wordbox.form.text',
    defaultMessage: 'Text',
  },
  enterText: {
    id: 'wordbox.form.enterText',
    defaultMessage: '1.) Vložte text',
  },
  markWords: {
    id: 'wordbox.form.markWords',
    defaultMessage: '2.) Označte kliknutím slová, ktoré sa budú dopĺňať',
  },
  addOption: {
    id: 'wordbox.form.addOption',
    defaultMessage: 'Pridať možnosť',
  },
  check: {
    id: 'wordbox.form.check',
    defaultMessage: 'Skontrolujte prosím, že sú po zmene textu označené správne slová.',
  },
  remind: {
    id: 'wordbox.form.remind',
    defaultMessage: 'Nezabudnite označiť napravo slová, ktoré majú žiaci dopĺňať.',
  },
  save: {
    id: 'wordbox.form.save',
    defaultMessage: 'Uložiť',
  },
})



// WEBPACK FOOTER //
// ./src/browser/forms/WordboxForm.js